import { useState, useCallback, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Zap, RotateCcw, CheckCircle, XCircle, ChevronLeft, ChevronRight, Brain } from 'lucide-react';
import { SPANISH_A1_VOCABULARY } from '@/data/spanish';
import type { VocabularyItem, ReviewResult } from '@/types/language';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

type PracticeMode = 'flashcard' | 'multiple_choice' | 'typing';

const PRACTICE_WORDS = SPANISH_A1_VOCABULARY;

const PracticePage = () => {
  const [mode, setMode] = useState<PracticeMode>('flashcard');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState<ReviewResult[]>([]);
  const [mcAnswer, setMcAnswer] = useState<number | null>(null);
  const [typingInput, setTypingInput] = useState('');
  const [showResult, setShowResult] = useState(false);

  useSeoMeta({
    title: 'Practice - LinguaNostr',
    description: 'Review vocabulary with spaced repetition flashcards.',
  });

  const currentWord: VocabularyItem | undefined = PRACTICE_WORDS[currentIndex];
  const isComplete = currentIndex >= PRACTICE_WORDS.length;

  // Generate wrong options for multiple choice
  const getOptions = useCallback((correct: VocabularyItem): string[] => {
    const others = PRACTICE_WORDS.filter((w) => w.id !== correct.id);
    const shuffled = others.sort(() => Math.random() - 0.5).slice(0, 3);
    const options = [correct.translation, ...shuffled.map((w) => w.translation)];
    return options.sort(() => Math.random() - 0.5);
  }, []);

  const handleNext = () => {
    setFlipped(false);
    setMcAnswer(null);
    setTypingInput('');
    setShowResult(false);
    setCurrentIndex((i) => i + 1);
  };

  const handleQuality = (quality: 0 | 1 | 2 | 3 | 4 | 5) => {
    if (!currentWord) return;
    setResults((prev) => [
      ...prev,
      {
        vocabularyId: currentWord.id,
        quality,
        responseTime: 0,
        timestamp: new Date().toISOString(),
      },
    ]);
    handleNext();
  };

  const handleMcSubmit = (index: number) => {
    setMcAnswer(index);
    setShowResult(true);
  };

  if (isComplete) {
    const correctCount = results.filter((r) => r.quality >= 3).length;
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="w-full max-w-lg border-slate-200 text-center dark:border-slate-800">
          <CardContent className="py-12">
            <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Session Complete!</h2>
            <p className="mt-2 text-slate-600 dark:text-slate-400">
              You reviewed {results.length} words ({correctCount} correct)
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Accuracy: {results.length > 0 ? Math.round((correctCount / results.length) * 100) : 0}%
            </p>
            <Button className="mt-6" onClick={() => { setCurrentIndex(0); setResults([]); }}>
              <RotateCcw className="mr-2 h-4 w-4" /> Start Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!currentWord) return null;
  const options = getOptions(currentWord);

  return (
    <div className="space-y-6">
      {/* Mode selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Practice</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Word {currentIndex + 1} of {PRACTICE_WORDS.length}
          </p>
        </div>
        <div className="flex gap-2">
          {(['flashcard', 'multiple_choice', 'typing'] as PracticeMode[]).map((m) => (
            <Button
              key={m}
              variant={mode === m ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode(m)}
            >
              {m === 'flashcard' ? 'Cards' : m === 'multiple_choice' ? 'MC' : 'Type'}
            </Button>
          ))}
        </div>
      </div>

      <Progress value={(currentIndex / PRACTICE_WORDS.length) * 100} className="h-2" />

      {/* Flashcard Mode */}
      {mode === 'flashcard' && (
        <Card
          onClick={() => setFlipped(!flipped)}
          className="group min-h-[300px] cursor-pointer border-slate-200 transition-all hover:shadow-lg dark:border-slate-800"
        >
          <CardContent className="flex min-h-[300px] flex-col items-center justify-center p-8">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {flipped ? 'Translation' : 'Spanish'}
            </p>
            <p className="mt-4 text-4xl font-bold text-slate-900 dark:text-white">
              {flipped ? currentWord.translation : currentWord.word}
            </p>
            {flipped && currentWord.mnemonic && (
              <p className="mt-4 max-w-md text-center text-sm italic text-slate-500 dark:text-slate-400">
                💡 {currentWord.mnemonic}
              </p>
            )}
            {flipped && currentWord.exampleSentences[0] && (
              <p className="mt-4 text-center text-sm text-slate-600 dark:text-slate-400">
                "{currentWord.exampleSentences[0].sentence}"
              </p>
            )}
            <p className="mt-6 text-xs text-slate-400">
              Tap to {flipped ? 'see word' : 'reveal translation'}
            </p>

            {/* Quality buttons */}
            {flipped && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {[
                  { q: 5 as const, label: 'Perfect', color: 'bg-green-500 hover:bg-green-600' },
                  { q: 4 as const, label: 'Easy', color: 'bg-blue-500 hover:bg-blue-600' },
                  { q: 3 as const, label: 'Good', color: 'bg-yellow-500 hover:bg-yellow-600' },
                  { q: 2 as const, label: 'Hard', color: 'bg-orange-500 hover:bg-orange-600' },
                  { q: 1 as const, label: 'Forgot', color: 'bg-red-500 hover:bg-red-600' },
                ].map(({ q, label, color }) => (
                  <Button
                    key={q}
                    size="sm"
                    className={color}
                    onClick={(e) => { e.stopPropagation(); handleQuality(q); }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Multiple Choice Mode */}
      {mode === 'multiple_choice' && (
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-8">
            <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">What does this word mean?</p>
            <p className="mb-6 text-3xl font-bold text-slate-900 dark:text-white">{currentWord.word}</p>
            <div className="space-y-3">
              {options.map((opt, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left text-base',
                    showResult && idx === currentWord.translation ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' :
                    mcAnswer === idx && !showResult ? 'border-blue-500' :
                    showResult && mcAnswer === idx && idx !== currentWord.translation ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300' :
                    ''
                  )}
                  onClick={() => !showResult && handleMcSubmit(idx)}
                  disabled={showResult}
                >
                  {String.fromCharCode(65 + idx)}. {opt}
                </Button>
              ))}
            </div>
            {showResult && (
              <Button className="mt-4 w-full" onClick={handleNext}>
                Next <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Typing Mode */}
      {mode === 'typing' && (
        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-8">
            <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">Type the translation</p>
            <p className="mb-4 text-3xl font-bold text-slate-900 dark:text-white">{currentWord.word}</p>
            <input
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-lg text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
              placeholder="Type translation..."
              value={typingInput}
              onChange={(e) => setTypingInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setShowResult(true);
                }
              }}
            />
            {showResult && (
              <div className="mt-4 space-y-4">
                <p className={cn(
                  'text-lg font-medium',
                  typingInput.toLowerCase().trim() === currentWord.translation.toLowerCase()
                    ? 'text-green-600' : 'text-red-600'
                )}>
                  {typingInput.toLowerCase().trim() === currentWord.translation.toLowerCase()
                    ? 'Correct!' : `Incorrect. Answer: ${currentWord.translation}`
                  }
                </p>
                <Button className="w-full" onClick={handleNext}>
                  Next <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
            {!showResult && (
              <Button className="mt-4 w-full" onClick={() => setShowResult(true)}>
                Check Answer
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats bar */}
      <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-yellow-500" />
          <span>Frequency rank: #{currentWord.frequencyRank}</span>
        </div>
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-purple-500" />
          <span className="capitalize">{currentWord.partOfSpeech}</span>
        </div>
      </div>
    </div>
  );
};

export default PracticePage;
